import { ANALYTIC_CODE } from '~/constants/analytic'

export default defineNuxtPlugin(() => {
  const analyticStore = useAnalyticStore()
  const ga4Info = computed(() => analyticStore.all.find(item =>
    `${item.code}` === ANALYTIC_CODE.GOOGLE_ANALYTIC_GA4 && item.enabled && item.trackId !== '',
  ))
  const gtmInfo = computed(() => analyticStore.all.find(item =>
    `${item.code}` === ANALYTIC_CODE.GOOGLE_TAG_MANAGER && item.enabled && item.trackId !== '',
  ))
  const facebookPixelInfo = computed(() => analyticStore.all.find(item =>
    `${item.code}` === ANALYTIC_CODE.FACEBOOK_PIXEL && item.enabled && item.trackId?.trim(),
  ))
  const tiktokInfo = computed(() => analyticStore.all.find(item =>
    `${item.code}` === ANALYTIC_CODE.TIKTOK_ANALYTIC && item.enabled && item.trackId !== '',
  ))

  const ga4 = useScriptGoogleAnalytics({
    id: ga4Info.value?.trackId || '',
    scriptOptions: {
      trigger: ga4Info.value?.trackId ? 'onNuxtReady' : 'manual',
    },
  })
  const gtm = useScriptGoogleTagManager({
    id: gtmInfo.value?.trackId || '',
    scriptOptions: {
      trigger: gtmInfo.value?.trackId ? 'onNuxtReady' : 'manual',
    },
  })

  useScriptEventPage(({ title, path }) => {
    if (gtmInfo.value?.trackId && gtm.proxy?.dataLayer) {
      gtm.proxy.dataLayer.push({
        event: 'page_view',
        page_title: title,
        page_location: path,
      })
    }
  })

  const fbPixel = useScriptMetaPixel({
    id: '',
    scriptOptions: {
      trigger: 'manual',
    },
  })

  let loadedFacebookPixelId = ''
  const loadFacebookPixel = (trackId: string) => {
    if (!trackId || trackId === loadedFacebookPixelId) return

    loadedFacebookPixelId = trackId

    const load = () => {
      if (facebookPixelInfo.value?.trackId?.trim() === trackId) {
        void fbPixel.load().then(() => {
          const fbq = (window as Window & {
            fbq?: (...args: unknown[]) => void
          }).fbq

          if (typeof fbq === 'function') {
            fbq('init', trackId)
            fbq('track', 'PageView')
          }
        })
      }
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(load, { timeout: 5000 })
    } else {
      setTimeout(load, 2000)
    }
  }

  watch(
    () => facebookPixelInfo.value?.trackId?.trim() || '',
    loadFacebookPixel,
    { immediate: true },
  )

  // TikTok Pixel — deferred to idle
  if (tiktokInfo.value?.trackId) {
    const trackId = tiktokInfo.value.trackId
    const loadTiktok = () => {
      const w = window as Window & { TiktokAnalyticsObject?: string, ttq?: Record<string, unknown> }
      const ttq = w.ttq = w.ttq || [] as unknown as Record<string, unknown>
      const methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie']
      const setAndDefer = (target: Record<string, unknown>, method: string) => {
        target[method] = function (...args: unknown[]) {
          (target as unknown as unknown[]).push([method, ...args])
        }
      }
      methods.forEach(method => setAndDefer(ttq, method))
      w.TiktokAnalyticsObject = 'ttq'
      useHead({
        script: [{ src: `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${trackId}&lib=ttq`, async: true }],
      })
      ;(ttq as unknown as { load: (id: string) => void }).load(trackId)
      ;(ttq as unknown as { page: () => void }).page()
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadTiktok, { timeout: 5000 })
    } else {
      setTimeout(loadTiktok, 2000)
    }
  }

  return {
    provide: {
      ga4,
      gtm,
      fbPixel,
    },
  }
})
